/** @format */

import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Input, Form, Button, message, Modal } from "antd";
import Header from "../../components/Header";
import "../../styles/contact.css";
import { base_url } from "../../constants/constant";
import { Axios } from "../../utils/axiosMiddleware";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";

export default function ContactUs() {
  const formRef = useRef();
  const [disable, setDisable] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { profileData } = useSelector((state) => state?.user);

  useEffect(() => {
    if (profileData) {
      form.setFieldsValue({
        FullName: profileData.profileName,
        email: profileData.email,
      });
    }
  }, [profileData, form]);

  const handleContactUsClick = () => {
    setIsModalVisible(true);
  };

  const onFinish = async (values) => {
    setDisable(true);
    try {
      const response = await Axios.post(`${base_url}tickets`, {
        name: values.FullName,
        email: values.email,
        description: values.message,
        status: "open",
        ticketType: "general_purpose",
        phoneNumber: values.phoneNumber,
      });
      message?.success(response?.data?.message);
      formRef.current.resetFields();
      setIsModalVisible(true);
      setDisable(false);
    } catch (err) {
      message?.error(err?.response?.data?.message);
      formRef.current.resetFields();
      setDisable(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Contact Sandseekers - Get In Touch for UAE Property Services
        </title>
        <meta
          name="description"
          content="Contact Sandseekers for expert real estate services in UAE. Dubai & Abu Dhabi offices. Call, email, or visit us for personalized property solutions."
        />
        <link rel="canonical" href="https://www.sandseekers.ae/contact-us" />

        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Contact Sandseekers - UAE Real Estate Services"
        />
        <meta
          property="og:description"
          content="Get in touch with Sandseekers for expert real estate services in Dubai & Abu Dhabi. Multiple office locations across UAE."
        />
        <meta
          property="og:url"
          content="https://www.sandseekers.ae/contact-us"
        />
        <meta property="og:site_name" content="Sandseekers" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="About Sandseekers - Your Trusted UAE Real Estate Partner"
        />
        <meta
          name="twitter:description"
          content="Expert real estate team with proven track record in Dubai & Abu Dhabi properties."
        />
      </Helmet>
      <Header />
      <Row>
        <Col span={24} className="topContainer">
          <img
            src="/icons/contactus.webp"
            alt="Contact Us"
            loading="eager"
            fetchPriority="high"
            className="topContainer-image"
          />
          <div className="topContainer-content">
            <span className="heading">
              <FormattedMessage id="contact_us" />
            </span>
            <span className="sub-heading">
              <FormattedMessage id="contact_us_sub_heading" />
            </span>
          </div>
        </Col>
      </Row>
      <Row className="bottomContainer">
        <Col xs={24} md={12} className="bottomLeftContainer">
          <span className="header">
            <FormattedMessage id="contact_us" />
          </span>
          <Form
            form={form}
            name="contact-form"
            initialValues={{
              FullName: profileData?.profileName,
              email: profileData?.email,
            }}
            onFinish={(e) => onFinish(e)}
            layout="vertical"
            className="formContainer"
            ref={formRef}
          >
            <Form.Item
              name="FullName"
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id="please_enter_name" />,
                },
                {
                  pattern: "^[a-zA-Z ]+$",
                  message: <FormattedMessage id="only_alpha" />,
                },
              ]}
            >
              <Input className="form" placeholder="Full Name" />
            </Form.Item>

            <Form.Item
              name="email"
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id="enter_emai_address" />,
                },
                {
                  type: "email",
                  message: <FormattedMessage id="valid_emai_address" />,
                },
              ]}
            >
              <Input className="form" placeholder="Email Address" />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id="phone_number_enter" />,
                },
                {
                  min: 10,
                  message: <FormattedMessage id="phone_number_must_be" />,
                },
              ]}
            >
              <PhoneInput
                inputProps={{
                  name: "phone",
                  required: true,
                }}
                countryCodeEditable={false}
                country={"ae"}
                prefix="+"
                inputStyle={{
                  width: "100%",
                  height: "50px",
                  backgroundColor: "white",
                  marginTop: "8px",
                  fontSize: "16px",
                  borderRadius: "5px"
                }}
              />
            </Form.Item>

            <Form.Item
              name="message"
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id="enter_message" />,
                },
              ]}
            >
              <Input.TextArea className="form" rows={5} placeholder="Message" />
            </Form.Item>

            <Form.Item>
              <Button
                className="formButton"
                loading={disable}
                htmlType="submit"
                onClick={handleContactUsClick}
              >
                <FormattedMessage id="send_message" />
              </Button>
            </Form.Item>
          </Form>
        </Col>
        <Col xs={0} md={1}></Col>
        <Col xs={24} md={11} className="bottomRightContainer">
          <span className="top-text">
            <FormattedMessage id="contact_us_p1" />
          </span>
          <span className="text-header">
            {" "}
            <FormattedMessage id="llc" />
          </span>
          <span className="bottom-text">
            <FormattedMessage id="contact_us_p2" />
          </span>
          <span className="end-text">
            <FormattedMessage id="thank_you" />
          </span>
        </Col>
      </Row>
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => setIsModalVisible(false)}
        bodyStyle={{
          height: "100px",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          alignItems: "center",
        }}
        okButtonProps={{ style: { backgroundColor: "#E5BE54" } }}
      >
        <p style={{ fontSize: "20px", width: "100%" }}>
          <FormattedMessage id="we_have_received" />
        </p>
      </Modal>
    </>
  );
}
